/* eslint-disable react/no-unstable-nested-components */

import { BottomTabScreenProps, createBottomTabNavigator } from '@react-navigation/bottom-tabs'
import { CompositeScreenProps, getFocusedRouteNameFromRoute, NavigatorScreenParams } from '@react-navigation/native'
import { Text, useTheme } from 'native-base'
import { ClipboardText, House, Lifebuoy, User } from 'phosphor-react-native'
import React, { lazy } from 'react'
import { useTranslation } from 'react-i18next'

import { RootStackParamList, RootStackScreenProps } from '..'
import { ConsultationStatus } from '../../@types'
import { useConsultations } from '../../data/use-consultations'
import { useTabScreenOptions } from '../../hooks/use-screen-options'

import { historyStackLinking, HistoryStackParamList } from './History'
import { homeStackLinking, HomeStackParamList } from './Home'
import { profileStackLinking, ProfileStackParamList } from './Profile'
import { supportStackLinking, SupportStackParamList } from './Support'

const History = lazy(() => import('./History'))
const Home = lazy(() => import('./Home'))
const Profile = lazy(() => import('./Profile'))
const Support = lazy(() => import('./Support'))

export type MainTabParamList = {
  HomeTab: NavigatorScreenParams<HomeStackParamList> | undefined
  HistoryTab: NavigatorScreenParams<HistoryStackParamList> | undefined
  ProfileTab: NavigatorScreenParams<ProfileStackParamList> | undefined
  SupportTab: NavigatorScreenParams<SupportStackParamList> | undefined
}

export type MainTabScreenProps<T extends keyof MainTabParamList> = CompositeScreenProps<
  BottomTabScreenProps<MainTabParamList, T>,
  RootStackScreenProps<keyof RootStackParamList>
>

export const mainTabsLinking = {
  screens: {
    HomeTab: homeStackLinking,
    HistoryTab: historyStackLinking,
    SupportTab: supportStackLinking,
    ProfileTab: profileStackLinking,
  },
}

const TabNavigator = createBottomTabNavigator()

export function MainTabs() {
  const { t } = useTranslation()
  const tabScreenOptions = useTabScreenOptions()
  const { colors } = useTheme()
  const { data } = useConsultations({
    status: [ConsultationStatus.QUEUED, ConsultationStatus.ACTIVE],
  })

  return (
    <TabNavigator.Navigator backBehavior="history" screenOptions={tabScreenOptions}>
      <TabNavigator.Screen
        name="HomeTab"
        component={Home}
        options={({ route }) => ({
          tabBarLabel: ({ focused }) => {
            return (
              <Text
                fontSize="xs"
                mt="2"
                color={focused ? 'primary.500' : 'neutral.700'}
                fontWeight={focused ? 'bold' : 'normal'}
                testID="home-tab"
              >
                {t('screens.howCanWeHelp.tab')}
              </Text>
            )
          },
          tabBarIcon: ({ color, focused }) => <House color={color} size={24} weight={focused ? 'bold' : 'regular'} />,
          tabBarStyle: {
            ...tabScreenOptions.tabBarStyle,
            display:
              !getFocusedRouteNameFromRoute(route) || getFocusedRouteNameFromRoute(route) === 'HowCanWeHelp'
                ? 'flex'
                : 'none',
          },
        })}
      />
      <TabNavigator.Screen
        name="HistoryTab"
        component={History}
        options={({ route }) => ({
          tabBarBadgeStyle: {
            minWidth: 8,
            minHeight: 8,
            maxWidth: 8,
            maxHeight: 8,
            borderRadius: 7,
            backgroundColor: colors.primary[500],
            left: 28,
            top: 0,
          },
          tabBarBadge: data?.consultations?.length ? '' : undefined,
          tabBarLabel: ({ focused }) => {
            return (
              <Text
                fontSize="xs"
                mt="2"
                color={focused ? 'primary.500' : 'neutral.700'}
                fontWeight={focused ? 'bold' : 'normal'}
                testID="history-tab"
              >
                {t('screens.consultations.tab')}
              </Text>
            )
          },
          tabBarIcon: ({ color, focused }) => (
            <ClipboardText color={color} size={24} weight={focused ? 'bold' : 'regular'} />
          ),
          tabBarStyle: {
            ...tabScreenOptions.tabBarStyle,
            display:
              !getFocusedRouteNameFromRoute(route) ||
              ['Consultations', 'Welcome'].includes(getFocusedRouteNameFromRoute(route) ?? '')
                ? 'flex'
                : 'none',
          },
        })}
      />
      <TabNavigator.Screen
        name="SupportTab"
        component={Support}
        options={() => ({
          tabBarLabel: ({ focused }) => {
            return (
              <Text
                fontSize="xs"
                mt="2"
                color={focused ? 'primary.500' : 'neutral.700'}
                fontWeight={focused ? 'bold' : 'normal'}
                testID="support-tab"
              >
                {t('screens.support.title')}
              </Text>
            )
          },
          tabBarIcon: ({ color, focused }) => (
            <Lifebuoy color={color} size={24} weight={focused ? 'bold' : 'regular'} />
          ),
        })}
      />
      <TabNavigator.Screen
        name="ProfileTab"
        component={Profile}
        options={({ route }) => ({
          tabBarLabel: ({ focused }) => {
            return (
              <Text
                fontSize="xs"
                mt="2"
                color={focused ? 'primary.500' : 'neutral.700'}
                fontWeight={focused ? 'bold' : 'normal'}
                testID="profile-tab"
              >
                {t('screens.profile.title')}
              </Text>
            )
          },
          tabBarIcon: ({ color, focused }) => <User color={color} size={24} weight={focused ? 'bold' : 'regular'} />,
          tabBarStyle: {
            ...tabScreenOptions.tabBarStyle,
            display:
              !getFocusedRouteNameFromRoute(route) ||
              ['Profile', 'Welcome'].includes(getFocusedRouteNameFromRoute(route) ?? '')
                ? 'flex'
                : 'none',
          },
        })}
      />
    </TabNavigator.Navigator>
  )
}

export default MainTabs
